import { modelOptions, post, prop } from "@typegoose/typegoose";
import { getZodSchemaFromClass, validate } from "../../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../../schemaBase.js";
import type {
    SproutVerificationChecksFieldsWithDefaults,
    SproutVerificationChecksSchemaTypes,
} from "../../../types/sproutVerificationChecks.d.js";
import type { DtoValidatorTypes } from "../../../validatableClass.js";
import { GeneralSproutVerificationCheck } from "./generalSproutVerificationCheck.js";

const SCHEMA_KEY = "SproutVerificationChecks" as const;

/**
 * Index of individual verification checks performed on different system components.
 *
 * Each verification instance runs multiple independent checks to validate different aspects
 * of the irrigation system's operation:
 *
 * - **valveState**: Verifies valves are in the correct open/closed positions
 * - **online**: Confirms device connectivity and recent communication
 * - **transitionOnTime**: Validates valve transitions occurred at the right time
 * - **sensor1/sensor2**: Checks sensor readings are within acceptable ranges
 * - **solenoid**: Verifies solenoid hardware is functioning properly
 *
 * Each check can have different statuses (successful, pending, error, etc.) and provides
 * detailed information about what was verified and why it passed or failed.
 *
 * @see GeneralSproutVerificationCheck - Individual check implementation and results
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SproutVerificationChecks>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutVerificationChecks extends SchemaBase<
    SproutVerificationChecks,
    SproutVerificationChecksFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /** Verification that valves are in the correct open/closed positions */
    @validate({
        childClass: GeneralSproutVerificationCheck,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => GeneralSproutVerificationCheck, required: false })
    public valveState?: GeneralSproutVerificationCheck;

    /** Verification of device connectivity and recent communication */
    @validate({
        childClass: GeneralSproutVerificationCheck,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => GeneralSproutVerificationCheck, required: false })
    public online?: GeneralSproutVerificationCheck;

    /** Verification that valve transitions occurred within the expected time window */
    @validate({
        childClass: GeneralSproutVerificationCheck,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => GeneralSproutVerificationCheck, required: false })
    public transitionOnTime?: GeneralSproutVerificationCheck;

    /** Verification of sensor 1 readings within acceptable range for current valve state */
    @validate({
        childClass: GeneralSproutVerificationCheck,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => GeneralSproutVerificationCheck, required: false })
    public sensor1?: GeneralSproutVerificationCheck;

    /** Verification of sensor 2 readings within acceptable range for current valve state */
    @validate({
        childClass: GeneralSproutVerificationCheck,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => GeneralSproutVerificationCheck, required: false })
    public sensor2?: GeneralSproutVerificationCheck;

    /** Verification of solenoid hardware operation during valve transitions */
    @validate({
        childClass: GeneralSproutVerificationCheck,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => GeneralSproutVerificationCheck, required: false })
    public solenoid?: GeneralSproutVerificationCheck;

    static getters = {} as const satisfies Record<string, (self: SproutVerificationChecks) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutVerificationChecksSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SproutVerificationChecks, SproutVerificationChecksFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutVerificationChecks.dtoValidators = getZodSchemaFromClass(
    SproutVerificationChecks,
) as unknown as DtoValidatorTypes<SproutVerificationChecksSchemaTypes>;
